import { apiClient } from './client'
import type { Comment, CommentImage } from './types'

export async function getComments(params: {
  requestId?: string
  requestItemId?: string
}): Promise<Comment[]> {
  if (params.requestId) {
    const res = await apiClient.get<{ success: true; data: Comment[] }>(
      `/requests/${params.requestId}/comments`,
    )
    return res.data.data
  }
  if (params.requestItemId) {
    const res = await apiClient.get<{ success: true; data: Comment[] }>(
      `/request-items/${params.requestItemId}/comments`,
    )
    return res.data.data
  }
  return []
}

export async function addComment(data: {
  text: string
  requestId?: string
  requestItemId?: string
  quoteId?: string | null
  mentions?: string[]
  parentId?: string | null
  /** Картинки, загруженные заранее (uploadCommentImage), — по их id. */
  imageIds?: string[]
}): Promise<Comment> {
  const res = await apiClient.post<{ success: true; data: Comment }>('/comments', data)
  return res.data.data
}

/** Картинка для комментария — грузится сразу при вставке, до отправки (с 29.10.2026). */
export async function uploadCommentImage(file: File): Promise<CommentImage> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await apiClient.post<{ success: true; data: CommentImage }>('/comments/images', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data.data
}

/** Адрес картинки комментария: раздаёт сервер с проверкой доступа. */
export function commentImageUrl(fileName: string): string {
  return `/api/uploads/comments/${fileName}`
}

/** Редактирование своего комментария (двойной клик в UI). Только автор. */
export async function updateComment(
  id: string,
  data: { text: string; mentions?: string[] },
): Promise<Comment> {
  const res = await apiClient.patch<{ success: true; data: Comment }>(`/comments/${id}`, data)
  return res.data.data
}

/** Удаление своего комментария. Только автор. Ответы остаются (parentId → null). */
export async function deleteComment(id: string): Promise<void> {
  await apiClient.delete(`/comments/${id}`)
}

/**
 * Список пользователей, которых можно @упомянуть в комментарии к заявке.
 * С `itemId` — к позиции: в списке ещё бухгалтер, логист и склад (с 21.10.2026).
 */
export async function getMentionableUsers(
  requestId: string,
  itemId?: string,
): Promise<{ id: string; name: string | null; email: string; role: string }[]> {
  const res = await apiClient.get<{
    success: true
    data: { id: string; name: string | null; email: string; role: string }[]
  }>(`/requests/${requestId}/mentionable-users`, itemId ? { params: { itemId } } : undefined)
  return res.data.data
}

/** Шапка окна «Переписка по позиции» — без цен (бухгалтер, логист, склад по @). */
export type ItemThreadHeader = {
  itemId: string
  requestId: string
  partNumber: string
  brand: string | null
  quantity: number
  requestNumber: string | null
  clientCode: string | null
  managerName: string | null
}

export async function getItemThread(itemId: string): Promise<ItemThreadHeader> {
  const res = await apiClient.get<{ success: true; data: ItemThreadHeader }>(
    `/request-items/${itemId}/thread`,
  )
  return res.data.data
}

/** Кого можно @упомянуть из окна «Переписка по позиции». */
export async function getItemMentionableUsers(
  itemId: string,
): Promise<{ id: string; name: string | null; email: string; role: string }[]> {
  const res = await apiClient.get<{
    success: true
    data: { id: string; name: string | null; email: string; role: string }[]
  }>(`/request-items/${itemId}/mentionable-users`)
  return res.data.data
}
